import { create } from 'zustand';
import type { User } from '@/api/types';
import { loadStoredToken, loadStoredUser, loginViaTelegram, persistToken, persistUser } from '@/lib/auth';
import { requestWriteAccess } from '@/lib/telegram';
import { configureApiClient } from '@/api/client';
import { api } from '@/api/endpoints';
import { configureTracker, setAnalyticsOptOut } from '@/lib/analytics';
import { useFeed } from './feed';

interface AuthState {
  token: string | null;
  user: User | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
  error?: string;

  hydrate: () => void;
  login: () => Promise<void>;
  refreshMe: () => Promise<void>;
  patchUser: (patch: Partial<User>) => void;
  logout: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  token: loadStoredToken(),
  user: loadStoredUser<User>(),
  status: 'idle',

  hydrate: () => {
    configureApiClient({
      getToken: () => get().token,
      onUnauthorized: async () => {
        // refresh через Telegram initData
        try {
          const prevUserId = get().user?.id ?? null;
          const prevLang = get().user?.lang ?? null;
          const { data } = await api.auth.telegram(window.Telegram?.WebApp?.initData ?? '');
          set({ token: data.token, user: data.user, status: 'ready' });
          persistToken(data.token);
          persistUser(data.user);
          // Если re-auth подменил юзера или язык — сбрасываем фид-кеш, иначе
          // карточки, загруженные под прошлой идентичностью/языком, продолжат
          // показываться (как было в баге с RU-карточками после онбординга на FR).
          if (data.user.id !== prevUserId || data.user.lang !== prevLang) {
            useFeed.getState().reset();
          }
        } catch {
          set({ token: null, user: null, status: 'error', error: 'unauthorized' });
          persistToken(null);
          persistUser(null);
        }
      },
      getLang: () => get().user?.lang ?? 'en',
    });
    configureTracker({
      getToken: () => get().token,
      optOut: get().user?.analytics_opt_out ?? false,
    });
  },

  login: async () => {
    set({ status: 'loading', error: undefined });
    try {
      const prevUserId = get().user?.id ?? null;
      const prevLang = get().user?.lang ?? null;
      const data = await loginViaTelegram();
      set({ token: data.token, user: data.user, status: 'ready' });
      // Первый вход: просим у Telegram разрешение боту писать в ЛС, чтобы
      // нотификации (реф-бонусы, ответы на комменты, стрик) доходили.
      if (data.is_new_user) void requestWriteAccess();
      if (data.user.id !== prevUserId || data.user.lang !== prevLang) {
        useFeed.getState().reset();
      }
    } catch (e) {
      set({ status: 'error', error: (e as Error).message });
      throw e;
    }
  },

  refreshMe: async () => {
    const { data } = await api.me.show();
    set({ user: data });
    persistUser(data);
    setAnalyticsOptOut(data.analytics_opt_out ?? false);
  },

  patchUser: (patch) => {
    const u = get().user;
    if (!u) return;
    const next = { ...u, ...patch };
    set({ user: next });
    persistUser(next);
    if (patch.analytics_opt_out !== undefined) {
      setAnalyticsOptOut(patch.analytics_opt_out);
    }
    // Смена языка инвалидирует фид-кеш: иначе после Profile→pickLang или
    // онбординг-Next карточки, уже лежащие в useFeed, останутся на старом языке
    // до перезапуска приложения.
    if (patch.lang !== undefined && patch.lang !== u.lang) {
      useFeed.getState().reset();
    }
  },

  logout: async () => {
    try { await api.auth.logout(); } catch { /* ignore */ }
    persistToken(null);
    persistUser(null);
    set({ token: null, user: null, status: 'idle' });
  },
}));
